import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext.js';
import LoadingScreen from '../components/LoadingScreen.js';
// Hooks (useTeamData se elimina, ahora está dentro de useDashboardData)
import useDashboardData from '../hooks/useDashboardData.js'; // <-- 1. IMPORTAR EL HOOK CORRECTO
import useFinancialSummary from '../hooks/useFinancialSummary.js'; // <-- IMPORTAR EL NUEVO HOOK
import useNotifications from '../hooks/useNotifications.js'; // <-- 2. IMPORTAR EL HOOK DE NOTIFICACIONES
// Componentes de Layout
import DashboardSidebar from '../context/DashboardSidebar.js';
import DashboardHeader from '../context/DashboardHeader.js';
import DashboardRouter from '../context/DashboardRouter.js';
import { getRankColor, getRemainingPointsForNextRank } from '../services/careerService.js';
import { RANKS, RANK_LIST } from '../constants/careerConfig.js';
import RankUpgradeModal from '../components/modals/RankUpgradeModal.js';
import { hasApprovedAgreement as checkApprovedAgreement } from '../utils/agreementAccess.js';

// Estilos
import '../styles/Dashboard.css';
import '../styles/global.css';
// Estilos específicos de las secciones (se mantienen para que las secciones los usen)
import '../styles/Finanzas.css';
import '../styles/Perfil.css';
import '../styles/Notificaciones.css';
import '../styles/PequenosDuenos.css';
import '../styles/Equipos.css';
import '../styles/MisAcuerdos.css';

const TEAM_AND_CAREER_SECTIONS = ['teams', 'rank-details'];
// Secciones que no aparecen hasta tener un depósito aprobado.
const APPROVED_ONLY_SECTIONS = ['finanzas', 'retiros', 'small-owners', 'club-beneficios', 'news', 'soporte'];

const Dashboard = () => {
    // --- CORRECCIÓN: Separar la obtención de datos ---
    // useAuth solo para autenticación y perfil.
    const { 
        currentUser: user, 
        userProfile, 
        loading,
        logout,
        isAdmin
    } = useAuth();
    
    // --- CORRECCIÓN: Usar el hook unificado para todos los datos del dashboard ---
    const { 
        agreements, 
        transactions, 
        withdrawals, 
        teamData, 
        loading: loadingDashboardData, // Renombrado para evitar conflictos
        error: errorDashboardData 
    } = useDashboardData(user);
    const { notifications, unreadCount } = useNotifications(user?.uid, userProfile?.isAdmin, { soundsEnabled: userProfile?.notificationSoundsEnabled !== false });

    // 2. Leer la ruta actual del navegador
    const location = useLocation();
    const getSectionFromPath = () => {
        const pathParts = location.pathname.split('/');
        return pathParts[2] || 'dashboard'; // Si la URL es /dashboard/perfil, devuelve 'perfil'
    };

    // 2. Estados específicos de la UI del Dashboard
    const [activeSection, setActiveSection] = useState(getSectionFromPath); // 3. Inicializar el estado con la sección de la URL
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    // --- LÓGICA DEL PLAN DE CARRERA ---
    const [showRankModal, setShowRankModal] = useState(false);
    const [achievedRank, setAchievedRank] = useState(null);
    const prevRankIdRef = useRef(null);

    useEffect(() => {
        const currentRankId = userProfile?.careerPath?.currentRankId || 0;

        // Inicializar la referencia en el primer render para evitar el modal al cargar
        if (prevRankIdRef.current === null && userProfile) {
            prevRankIdRef.current = currentRankId;
            return;
        }

        // Detectar si hubo un ascenso (ID actual mayor al previo)
        if (userProfile && currentRankId > prevRankIdRef.current) {
            const rankInfo = Object.values(RANKS).find(r => r.id === currentRankId);
            if (rankInfo) {
                setAchievedRank(rankInfo);
                setShowRankModal(true);
            }
            prevRankIdRef.current = currentRankId;
        }
    }, [userProfile?.careerPath?.currentRankId, userProfile]);

    // Equipo de Trabajo y Plan de Carrera quedan ocultos para todos salvo admins y los usuarios
    // que un admin habilite desde Gestión de Usuarios (canSeeTeamAndCareer en el perfil).
    // Solo se oculta la vista: comisiones, puntos y rangos se siguen calculando igual.
    const canSeeTeamAndCareer = isAdmin || userProfile?.canSeeTeamAndCareer === true;

    // 3. Hooks personalizados para obtener datos derivados o adicionales
    const { kpiData: rawKpiData, financialSummary, chartData } = useFinancialSummary(agreements, transactions, userProfile);

    // Extender los KPIs con los Puntos de Carrera acumulados del Plan de Carrera
    const kpiData = useMemo(() => {
        const points = userProfile?.careerPath?.accumulatedPoints || 0;
        const currentRankId = userProfile?.careerPath?.currentRankId || 0;

        // Buscamos el siguiente rango en la lista oficial
        const nextRank = RANK_LIST.find(rank => rank.id > currentRankId);

        let displayValue = points.toLocaleString();

        // Si existe un rango superior, inyectamos la barra de progreso en el valor del KPI
        if (nextRank) {
            const percentage = Math.min(Math.round((points / nextRank.pointsRequired) * 100), 100);
            const barColor = getRankColor(nextRank.id); // Color del objetivo actual
            const remainingPoints = getRemainingPointsForNextRank(points, currentRankId);

            displayValue = (
                <div className="career-progress-kpi" style={{ width: '100%' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>{points.toLocaleString()}</div>
                    <div className="progress-bar-wrapper" style={{ height: '8px', marginTop: '10px', backgroundColor: '#f3f4f6' }}>
                        <div 
                            className="progress-bar" 
                            style={{ width: `${percentage}%`, height: '100%', backgroundColor: barColor }} 
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginTop: '5px', color: '#6b7280', fontWeight: 'normal' }}>
                        <span>Sig: {nextRank.label} ({remainingPoints?.toLocaleString()} pts faltan)</span>
                        <span>{percentage}%</span>
                    </div>
                </div>
            );
        }

        return {
            ...(rawKpiData || {}),
            careerPoints: displayValue
        };
    }, [rawKpiData, userProfile]);

    // 4. Datos calculados simples (unreadCount ahora viene del hook useNotifications)
    const hasApprovedAgreement = useMemo(() => checkApprovedAgreement(agreements), [agreements]);

    // Sin depósito aprobado no se ven Finanzas, Mis Retiros, Pequeños Dueños, Club de Beneficios, Noticias ni Soporte.
    const canSeeApprovedSections = isAdmin || hasApprovedAgreement;
    const isHiddenSection = (section) =>
        (!canSeeTeamAndCareer && TEAM_AND_CAREER_SECTIONS.includes(section)) ||
        (!canSeeApprovedSections && APPROVED_ONLY_SECTIONS.includes(section));
    const visibleSection = isHiddenSection(activeSection) ? 'dashboard' : activeSection;

    // 5. Efecto secundario para actualizar el título de la página
    useEffect(() => {
        const originalTitle = "Lignums Group - Dashboard";
        document.title = unreadCount > 0 ? `(${unreadCount}) ${originalTitle}` : originalTitle;
    }, [unreadCount]);
    
    // 6. Si los datos esenciales aún están cargando, mostrar la pantalla de carga.
    // Este guard se dispara ANTES de que exista el sidebar/header (todavía no se montó nada del
    // layout del dashboard), así que un esqueleto con grillas dependientes del ancho del
    // contenedor (como DashboardSkeleton) se mide sin ningún contexto de layout y se ve roto
    // (columnas colapsadas a un ancho mínimo). LoadingScreen es un overlay fijo a toda la
    // pantalla, no depende de nada de eso.
    if (loading || loadingDashboardData || !userProfile) { // Usamos el estado de carga unificado
        return <LoadingScreen message="Cargando tu panel..." />;
    }

    // 7. Handlers para la interacción del usuario
    const handleNavClick = (section) => {
        if (isHiddenSection(section)) return;
        const lockedSections = ['finanzas', 'retiros', 'teams', 'small-owners'];
        if (lockedSections.includes(section) && !hasApprovedAgreement && !userProfile.isAdmin) { // Admin can access all
            Swal.fire({ title: 'Módulo Bloqueado', html: 'Debes tener un aporte aprobado para acceder.', icon: 'info' });
            return;
        }
        setActiveSection(section);
        setMobileMenuOpen(false);
    };

    const handleNotificationClick = () => {
        // Navega a la sección de notificaciones y cierra el dropdown
        handleNavClick('notificaciones');
    };

    // 8. Renderizado del layout principal
    return (
        <div className="dashboard-container">
            <DashboardSidebar 
                activeSection={visibleSection}
                handleNavClick={handleNavClick}
                isMobileMenuOpen={isMobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
                hasApprovedAgreement={hasApprovedAgreement}
                canSeeTeamAndCareer={canSeeTeamAndCareer}
                canSeeApprovedSections={canSeeApprovedSections}
            />
            <div className="main-area">
                <DashboardHeader 
                    userProfile={userProfile}
                    unreadCount={unreadCount}
                    notifications={notifications}
                    setMobileMenuOpen={setMobileMenuOpen}
                    handleNotificationClick={handleNotificationClick}
                    onLogout={logout}
                />
                <main className="main-content">
                    <p className="internal-tool-title">Herramienta de uso interno</p>
                    {/* --- CORRECCIÓN: Pasar los acuerdos enriquecidos al router --- */}
                    <DashboardRouter {...{ 
                        activeSection: visibleSection, 
                        canSeeTeamAndCareer,
                        handleNavClick, 
                        user, 
                        userProfile,
                        agreements: agreements, // <-- Usamos la lista original
                        transactions, 
                        withdrawals, 
                        kpiData, 
                        financialSummary, 
                        chartData, 
                        teamData, loadingTeam: loadingDashboardData, errorTeam: errorDashboardData }} />
                </main>
            </div>

            {/* Modal de Felicitación por Nuevo Rango Alcanzado */}
            {showRankModal && achievedRank && canSeeTeamAndCareer && (
                <RankUpgradeModal 
                    rank={achievedRank} 
                    onClose={() => setShowRankModal(false)} 
                />
            )}
        </div>
    );
};

export default Dashboard;